import { useWeb3React } from '@web3-react/core'
import { Button } from 'components/ui/button'
import { useCallback, useEffect, useState } from 'react'
import {
  useIsAdmin,
  useIsUserSignedIn,
  useIsUserTotpValidated,
} from 'state/user/hooks'
import useAuth from 'hooks/useAuth'
import { ConnectorNames } from 'constants/types'
import useTotp, { useSubmitToken } from '../../state/totp/hooks'
import { useTheme } from '@mui/material'
import { useAppDispatch } from 'state/hooks'
import OtpInput from 'react-otp-input'
import { fetchTOTPStatusData } from 'state/totp/reducer'
import { useNavigate } from 'react-router-dom'
import useSignOAuth from 'hooks/useSignOAuth'
import useToast from 'hooks/useToast'
import TermsDisclaimerDialog from './TermsDisclaimerDialog'

export default function CuratorLoginPage() {
  const navigate = useNavigate()

  const { signOAuth } = useSignOAuth()

  const isUserSignedIn = useIsUserSignedIn()
  const isAdmin = useIsAdmin()
  const isTotpValidated = useIsUserTotpValidated()
  const { account } = useWeb3React()
  const { login, logout } = useAuth()
  const { showToast } = useToast()

  const [otpToken, setOtpToken] = useState('')
  const [doneScan, setDoneScan] = useState(false)
  const [invalidCode, setInvalidCode] = useState(false)
  const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false)
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false)
  const [showTermsNotice, setShowTermsNotice] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)

  const { submitOtp } = useSubmitToken()

  const theme = useTheme()

  const dispatch = useAppDispatch()
  useEffect(() => {
    if (isUserSignedIn && isAdmin) {
      dispatch(fetchTOTPStatusData())
    }
  }, [dispatch, isUserSignedIn, isAdmin])

  useEffect(() => {
    if (!isUserSignedIn) {
      setDisclaimerAccepted(false)
      setShowDisclaimerDialog(false)
      setShowTermsNotice(false)
      setOtpVerified(false)
    } else {
      const termsAccepted = localStorage.getItem('curatorTermsAccepted')
      if (termsAccepted === 'true') {
        setDisclaimerAccepted(true)
      }
    }
  }, [isUserSignedIn])

  const otp = useTotp()

  const signUser = useCallback(
    async (connectorId: ConnectorNames) => {
      if (!account) {
        await login(connectorId)
      }
    },
    [account, login],
  )

  const resetDisclaimerState = useCallback(() => {
    setDisclaimerAccepted(false)
    setShowTermsNotice(false)
    setShowDisclaimerDialog(false)
    setOtpVerified(false)
  }, [])

  const handleLogoutWithReset = useCallback(() => {
    resetDisclaimerState()
    setDoneScan(false)
    logout()
  }, [logout, resetDisclaimerState])

  const handleDisclaimerAccept = useCallback(() => {
    setDisclaimerAccepted(true)
    setShowDisclaimerDialog(false)
    setShowTermsNotice(false)

    localStorage.setItem('curatorTermsAccepted', 'true')

    if (otpVerified) {
      showToast({
        message: account ? `Welcome ${account}` : 'Welcome',
        type: 'success',
      })
      navigate('/deals')
    }
  }, [otpVerified, navigate, showToast, account])

  const handleDisclaimerClose = useCallback(() => {
    if (!disclaimerAccepted && otpVerified) {
      setShowTermsNotice(true)
    }
    setShowDisclaimerDialog(false)
  }, [disclaimerAccepted, otpVerified])

  const handleViewTerms = useCallback(() => {
    setShowTermsNotice(false)
    setShowDisclaimerDialog(true)
  }, [])

  const handleOtpSuccess = useCallback(() => {
    setOtpVerified(true)

    if (disclaimerAccepted) {
      navigate('/deals')
      return
    }

    setShowDisclaimerDialog(true)
    setShowTermsNotice(false)
  }, [disclaimerAccepted, navigate])

  useEffect(() => {
    if (isUserSignedIn && isAdmin && isTotpValidated && !otpVerified) {
      handleOtpSuccess()
    }
  }, [isUserSignedIn, isAdmin, isTotpValidated, otpVerified, handleOtpSuccess])

  if (!otp) {
    return null
  }
  const { state, qrCode } = otp

  return (
    <>
      <div className="min-h-screen bg-neutral-50 flex">
        <div
          className="bg-brand-primary flex flex-col rounded-lg my-4 px-0 mx-4 w-[45%] relative"
          style={{
            backgroundImage: 'url(/images/curated-pattern.svg)',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
            backgroundSize: 'auto',
          }}
        >
          <div className="absolute top-6 left-10">
            <img
              src="/images/curated-curator-portal-white.svg"
              alt="CURATED"
              width={161}
              height={34}
            />
          </div>

          <div className="flex-1 flex items-center justify-start pl-10">
            <div className="text-white space-y-0 max-w-md">
              <div className="flex justify-center"></div>
              <div className="space-y-4">
                <div className="flex justify-start">
                  <img
                    src="/images/curated-curator-portal-text.svg"
                    alt="CURATED Curator Portal"
                    width={370}
                    height={64}
                  />
                </div>
                <p className="opacity-90 text-left text-base font-normal">
                  Manage token sales and submissions in one place
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-screen flex items-center justify-center p-8 w-[55%]">
          <div className="w-full max-w-md">
            <div className="relative h-[400px]">
              <div className="absolute top-[120px] left-0 right-0">
                {!account ? (
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-neutral-900">
                      Log In
                    </h2>
                    <p className="text-neutral-600">
                      Login with your authorized wallet
                    </p>
                  </div>
                ) : !isUserSignedIn ? (
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-neutral-900">
                      Sign In
                    </h2>
                    <p className="text-neutral-600">
                      Sign in with your wallet, no gas fee required.
                    </p>
                  </div>
                ) : !isAdmin ? (
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-neutral-950">
                      Access Denied
                    </h2>
                    <p className="text-neutral-600">
                      This wallet is not authorized to access the portal
                    </p>
                  </div>
                ) : !otpVerified ? (
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-neutral-900">
                      Verify your identity
                    </h2>
                    <p className="text-neutral-600">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>
                ) : (
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-neutral-900">
                      Welcome!
                    </h2>
                    <p className="text-neutral-600">
                      Identity verified successfully
                    </p>
                  </div>
                )}
              </div>

              <div className="absolute top-[200px] left-0 right-0">
                {!account ? (
                  <div className="text-center space-y-3">
                    <Button
                      onClick={() => signUser(ConnectorNames.Injected)}
                      className="w-[320px] bg-brand-primary hover:bg-brand-primary/90 text-white"
                    >
                      Connect with MetaMask
                    </Button>
                    <Button
                      onClick={() => signUser(ConnectorNames.WalletConnect)}
                      className="w-[320px] bg-white border border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                    >
                      Connect with WalletConnect
                    </Button>
                  </div>
                ) : !isUserSignedIn ? (
                  <div className="text-center space-y-2">
                    <Button
                      onClick={() => signOAuth()}
                      className="w-[320px] bg-brand-primary hover:bg-brand-primary/90 text-white"
                    >
                      Sign Message
                    </Button>
                  </div>
                ) : !isAdmin ? (
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Button
                        onClick={handleLogoutWithReset}
                        className="w-[320px] bg-brand-primary hover:bg-brand-primary/90 text-white mx-auto block"
                      >
                        Try Different Wallet
                      </Button>

                      <div className="text-center">
                        <a
                          href="mailto:cs@impossible.finance"
                          className="text-sm text-brand-primary hover:text-brand-primary/80 underline"
                        >
                          Contact Support
                        </a>
                      </div>
                    </div>
                  </div>
                ) : showTermsNotice ? (
                  <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                    <p className="text-sm text-neutral-700">
                      You must acknowledge the Terms of Use before accessing the
                      Curator Portal.
                    </p>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleViewTerms}
                        className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                      >
                        View Terms
                      </Button>
                      <button
                        onClick={handleLogoutWithReset}
                        className="text-sm text-neutral-600 hover:text-neutral-900 underline"
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                ) : state === 'new' && !doneScan ? (
                  <div className="flex justify-center flex-col items-center">
                    <img
                      src={`data:image/png;base64, ${qrCode}`}
                      alt="qcCode"
                    />
                    <div className="mt-4">
                      <Button
                        onClick={() => setDoneScan(true)}
                        className="w-[320px] bg-brand-primary hover:bg-brand-primary/90 text-white mx-auto block"
                      >
                        Enter Code
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-center gap-2 mb-4">
                      <OtpInput
                        value={otpToken}
                        onChange={(val) => {
                          setOtpToken(val)
                          setInvalidCode(false)
                        }}
                        numInputs={6}
                        isInputNum
                        hasErrored={invalidCode}
                        errorStyle={{
                          border: 'solid 1px #E57373',
                        }}
                        inputStyle={{
                          background: 'white',
                          height: 48,
                          width: 48,
                          fontSize: 20,
                          color: '#000',
                          border: 'solid 1px rgb(212, 212, 212)',
                          margin: '0px 10px',
                          borderRadius: 8,
                          [theme.breakpoints.down('sm')]: {
                            height: 48,
                            width: 48,
                            fontSize: 16,
                            margin: '0px 5px',
                          },
                        }}
                      />
                    </div>

                    <Button
                      onClick={() => {
                        submitOtp(
                          {
                            otpCode: otpToken,
                          },
                          handleOtpSuccess,
                          () => {
                            setInvalidCode(true)
                          },
                        )
                      }}
                      className="w-[320px] bg-brand-primary hover:bg-brand-primary/90 text-white mx-auto block"
                    >
                      Verify
                    </Button>

                    <div className="text-center space-y-1">
                      {/* <button
                        className="text-sm text-brand-primary hover:text-brand-primary/80"
                        onClick={() => {
                          setDoneScan(false)
                        }}
                      >
                        Scan Again
                      </button> */}
                      <div>
                        <button
                          onClick={handleLogoutWithReset}
                          className="text-sm text-neutral-600 hover:text-neutral-800 underline"
                        >
                          Change Wallet
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <TermsDisclaimerDialog
        open={showDisclaimerDialog}
        onAccept={handleDisclaimerAccept}
        onClose={handleDisclaimerClose}
      />
    </>
  )
}
